import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { Customer } from '@wsphere/customers/domain';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, merge, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { CustomerService } from '../../services';

@Component({
  selector: 'ws-customer-picker',
  templateUrl: './customer-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: CustomerPickerComponent,
    },
  ],
})
export class CustomerPickerComponent implements ControlValueAccessor, OnDestroy {
  constructor(
    public elementRef: ElementRef,
    private customerSvc: CustomerService,
    private breakpointObserver: BreakpointObserver
  ) {}

  @Input() openClaim = false;

  @Output() openChanged = new EventEmitter<boolean>();
  @Output() valueChanges = new EventEmitter<Customer.Model | null>();

  @PropertyListener('value') value$ = new ReplaySubject<Customer.Model>(1);
  @Input() value: Customer.Model | null = null;

  readonly searchQuery$ = new BehaviorSubject<string>('');
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  readonly loading$ = this.loadingSubject.asObservable();

  private readonly searchTriggers$ = merge(this.searchQuery$, this.value$.pipe(map(value => value?.name ?? '')));

  overlayOpen = false;

  // Calculate initial limit based on screen size; the dropdown itself scrolls, so this only
  // controls how many results come back before the user needs to hit "Load More"
  private getInitialLimit(): number {
    const isXSmall = this.breakpointObserver.isMatched(Breakpoints.XSmall); // < 600px
    const isSmall = this.breakpointObserver.isMatched(Breakpoints.Small); // 600px - 960px
    const isMedium = this.breakpointObserver.isMatched(Breakpoints.Medium); // 960px - 1280px

    if (isXSmall) return 3;
    if (isSmall) return 4;
    if (isMedium) return 6;
    return 8; // Large screens
  }

  private initialLimit = this.getInitialLimit();
  private currentPage$ = new BehaviorSubject<number>(1);
  private allCustomers$ = new BehaviorSubject<Customer.Model[]>([]);
  private totalItems$ = new BehaviorSubject<number>(0);
  readonly hasMore$ = new BehaviorSubject<boolean>(false);
  readonly loadingMore$ = new BehaviorSubject<boolean>(false);

  private fetchTrigger$ = combineLatest([this.searchTriggers$, this.currentPage$]).pipe(
    debounceTime(300),
    distinctUntilChanged(
      ([prevSearch, prevPage], [currSearch, currPage]) => prevSearch === currSearch && prevPage === currPage
    ),
    switchMap(([search, page]) => {
      if (page === 1) {
        this.loadingSubject.next(true);
      } else {
        this.loadingMore$.next(true);
      }
      return this.customerSvc.listCustomers$({ search, page, limit: this.initialLimit }, this.openClaim).pipe(
        map(response => ({ response, page, search })),
        tap(() => {
          this.loadingSubject.next(false);
          this.loadingMore$.next(false);
        }),
        catchError(() => {
          this.loadingSubject.next(false);
          this.loadingMore$.next(false);
          return of({ response: { items: [], meta: { totalItems: 0 } }, page, search });
        })
      );
    })
  );

  customers$: Observable<Customer.Model[]> = this.fetchTrigger$.pipe(
    withLatestFrom(this.value$.pipe(startWith(this.value ?? null))),
    switchMap(([{ response, page, search: _search }, selected]) => {
      if (page === 1) {
        // Reset customers on first page (new search)
        this.allCustomers$.next(response.items || []);
      } else {
        // Append customers for subsequent pages
        const current = this.allCustomers$.value;
        this.allCustomers$.next([...current, ...(response.items || [])]);
      }

      this.totalItems$.next(response.meta?.totalItems || 0);

      // Check if there are more items to load
      const totalLoaded = this.allCustomers$.value.length;
      const hasMore = totalLoaded < (response.meta?.totalItems || 0);
      this.hasMore$.next(hasMore);

      const customers = this.allCustomers$.value;

      // Ensure selected customer is in the list if not found
      if (selected && !customers.find(customer => customer.userId === selected.userId)) {
        return this.customerSvc
          .listCustomers$({ search: selected.name ?? '', page: 1, limit: 1 }, this.openClaim)
          .pipe(map(additional => [additional.items?.[0], ...customers].filter(Boolean) as Customer.Model[]));
      }

      return of(customers);
    })
  );

  @Input() placeholder = 'Find a customer...';
  @Input() required = false;
  @Input() disabled = false;
  @Input() themeColor: ThemeColor = 'primary';
  touched = false;

  @Input() compareWith: (v0: Customer.Model | null, v1: Customer.Model | null) => boolean = (
    v0: Customer.Model | null,
    v1: Customer.Model | null
  ) => {
    switch (typeof v0) {
      case 'object':
        return JSON.stringify(v0) === JSON.stringify(v1);
      default:
        return v0 === v1;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange = (_value: Customer.Model | null) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  selectValueChanged(value: Customer.Model) {
    this.markAsTouched();
    this.value?.userId === value?.userId ? (this.value = null) : (this.value = value);
    this.close();
    this.onChange(this.value);
    this.valueChanges.next(this.value);
  }

  onInputChanged(value: string) {
    this.searchQuery$.next(value);
    this.currentPage$.next(1); // Reset to first page on new search

    // When the input is cleared, also clear the selected customer to reset filters
    if (!value?.trim() && this.value) {
      this.value = null;
      this.onChange(this.value);
      this.valueChanges.next(this.value);
    }
  }

  clearSelection(input: HTMLInputElement) {
    input.value = '';
    this.searchQuery$.next('');
    this.currentPage$.next(1); // Reset to first page

    if (this.value) {
      this.value = null;
      this.onChange(this.value);
      this.valueChanges.next(this.value);
    }
  }

  loadMore() {
    if (this.hasMore$.value && !this.loadingMore$.value) {
      this.currentPage$.next(this.currentPage$.value + 1);
    }
  }

  open() {
    this.overlayOpen = true;
  }

  close() {
    this.overlayOpen = false;
  }

  toggle() {
    this.overlayOpen = !this.overlayOpen;
  }

  writeValue(value: Customer.Model | null): void {
    this.value = value;
  }
  registerOnChange(onChange: (_value: Customer.Model | null) => {}): void {
    this.onChange = onChange;
  }
  registerOnTouched(onTouched: () => {}): void {
    this.onTouched = onTouched;
  }
  setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
  }
  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  ngOnDestroy(): void {
    this.value$.complete();
    this.searchQuery$.complete();
    this.loadingSubject.complete();
    this.currentPage$.complete();
    this.allCustomers$.complete();
    this.totalItems$.complete();
    this.hasMore$.complete();
    this.loadingMore$.complete();
  }
}
